import { AggregationLevel } from "@/pages/analysis";

export const CampaignTypeValues = ["all", "retargeting", "impact", "install", "ctv_install"] as const;
export type CampaignType = (typeof CampaignTypeValues)[number];

export const CampaignTypeValuesWithoutAll = ["retargeting", "impact", "install", "ctv_install"] as const;
export const CampaignTypeValuesInstallOnly = ["install", "ctv_install", "all"] as const;

type RevenueHistoryRow = {
  timestamp: string;
  organization: string;
  audience: string;
  bids: number;
  impressions: number;
  ap_conversions: number;
  skan_installs: number;
  buying_price_eur: number;
  win_price_eur: number;
  revenue: number;
};

type QueryIntervalParams = {
  start: string;
  end: string;
  granularity: Granularity;
};

type Granularity = "all" | "none" | "minute" | "fifteen_minute" | "thirty_minute" | "hour" | "day" | "month";

type AudienceCampaignOptionData = {
  audience_id: string;
  organization_id: string;
  audience_name: string;
  campaign_id: string;
  campaign_name: string;
  campaign_type: number;
};

type AudienceOptionsParams = {
  campaign_type: string;
};

type PerformanceParams = {
  audience_id?: string;
  start_date?: string;
  end_date?: string;
  campaign_id?: string;
  campaign_type?: string;
};

type AnalysisParams = {
  audience_id?: string;
  campaign_id?: string;
  start_date?: string;
  end_date?: string;
  campaign_type?: string;
  dimension: string;
  granularity: AggregationLevel;
};

type CampaignFilterParams = {
  campaign_id?: string;
  start_date?: string;
  end_date?: string;
};

type MetaQueryData = {
  TotalBytesProcessed: number;
  CostUSD: number;
  Took: number;
};

type ResourceOptions = {
  id: string;
  name: string;
};

type AudienceCampaignOption = {
  audience_name: string;
  organization_id: string;
  campaigns: ResourceOptions[];
};

type AudinceCampaignOptionResponse = {
  params: AudienceOptionsParams;
  meta: MetaQueryData;
  results: AudienceCampaignOptionData[];
};

export type CalibrationAnalysisParams = {
  start_date?: string;
  end_date?: string;
  audience: number;
  campaign: number;
  campaign_type?: string;
};

export type CalibrationAnalysisResponse = {
  params: CalibrationAnalysisParams;
  meta: MetaQueryData;
  results: CalibrationTableRow[];
};

export type CalibrationTableRow = {
  InstallsPerCostBucket: string;
  AdType: string;
  AvgBidPriceCpmMicroEur: number;
  AvgImpValueMicroEur: number;
  AvgMaxBidMicroEur: number;
  AvgSpendAdjustment: number;
  AvgCorrectionCoefficient: number;
  AvgEstimatedWinRate: number;
  AvgWinPriceCpmMicroEur: number;
  AvgTotalBidMultiplier: number;
  AvgDynamicTargetMargin: number;
  WinRate: number;
  Bids: number;
  Impressions: number;
  ImpPerBids: number;
  SupplyCostEur: number;
  RevenueEur: number;
  ProfitEur: number;
  EstimatedInfraCostEur: number;
  GrossProfitEur: number;
  Conversions: number;
  ApConversions: number;
  AvgEstimatedIpm: number;
  ApIpm: number;
  ApCpiEur: number;
  PredictedBidCpiEur: number;
  PredictedImpCpiEur: number;
  InstallsPerCost: number;
  InstallsPerCostCalibration: number;
  CostPerInstallCalibration: number;
  InstallRateCalibration: number;
  WinRateCalibration: number;
  ImpressionsPerBidsCalibration: number;
  WinPriceCalibration: number;
  Margin: number;
  GrossMargin: number;
};

export type CalibrationPlotsResponse = {
  params: CalibrationAnalysisParams;
  meta: MetaQueryData;
  results: CalibrationPlotRow[];
};

export type CalibrationPlotRow = {
  AdType: string;
  VolumeQuantileBucket: string;
  AvgEstimatedIpm: number;
  ApIpm: number;
  AvgEstimatedWinRate: number;
  WinRate: number;
  ImpPerBids: number;
  AvgBidPriceCpmMicroEur: number;
  RevenueEur: number;
  ActualCpiEur: number;
  PredictedCpiEur: number;
  TotalBids: number;
  TotalImpressions: number;
  TotalApConversions: number;
};

export type ConversionAvailabilityRow = {
  bid_ts_hour: string;
  ap_conversions_total: number;
  secondary_ap_conversions_total: number;
  ap_conversions_with_bid_ts: number;
  secondary_ap_conversions_with_bid_ts: number;
};

export type ConversionTimingHistogramRow = {
  time_bucket: string;
  ap_conversions_count: number;
  secondary_ap_conversions_count: number;
  ap_percentage: number;
  secondary_ap_percentage: number;
  ap_avg_time_seconds: number;
  ap_median_time_seconds: number;
  secondary_ap_avg_time_seconds: number;
  secondary_ap_median_time_seconds: number;
  ap_total_with_timing: number;
  secondary_ap_total_with_timing: number;
};

export type PerformanceResponse = {
  params: PerformanceParams;
  revenue: RevenueData;
  revenue_by_ad_type: RevenueByAdTypeData;
  conversion_availability: ConversionAvailabilityData;
  timing_histogram: ConversionTimingHistogramData;
};

export type RevenueData = {
  results: RevenueRow[];
  meta: MetaQueryData;
};

export type ConversionAvailabilityData = {
  results: ConversionAvailabilityRow[];
  meta: MetaQueryData;
};

export type ConversionTimingHistogramData = {
  results: ConversionTimingHistogramRow[];
  meta: MetaQueryData;
};

export type RevenueByAdTypeData = {
  results: RevenueByAdTypeRow[];
  meta: MetaQueryData;
};

type ApiResponse<T> = {
  params: object;
  meta: MetaQueryData;
  results: T[];
};

type RevenueRow = {
  campaign_id: string;
  bid_day: string;
  revenue_eur: number;
  ap_target_cpx_eur: number;
  secondary_ap_target_cpx_eur: number;
  ap_conversions: number;
  secondary_ap_conversions: number;
  cpi: number;
  secondary_cpa: number;
  cpi_achievement: number;
  secondary_cpa_achievement: number;
};

type RevenueByAdTypeRow = {
  ad_type: string;
  placement_group: string;
  revenue_eur: number;
  ap_conversions: number;
  secondary_ap_conversions: number;
};

type RevenueOverviewRow = {
  month_year: string;
  revenue: number;
  infrastructure_cost_share: number;
  query_period: string;
  bid_day: string;
};

type OrganizationInfo = {
  id: number;
  name: string;
};

type ClientMetricsRow = {
  month_year: string;
  active_organizations: OrganizationInfo[];
  high_performance_organizations: OrganizationInfo[];
  new_clients: OrganizationInfo[];
  new_clients_high_performance_count: number;
  new_clients_total_count: number;
  active_audiences: OrganizationInfo[];
  high_performance_audiences: OrganizationInfo[];
  new_audiences: OrganizationInfo[];
};

type OverviewResponse = {
  params: object;
  meta: MetaQueryData;
  revenue: RevenueOverviewRow[];
  clients: ClientMetricsRow[];
};

type ClientRow = {
  month_year: string;
  organization_id: string;
  organization_name: string;
  name: string;
  active_clients: number | null;
  new_clients: number | null;
};

type BiddingEfficiencyParams = {
  start_date: string;
  end_date: string;
  audience: number;
  campaign: number;
  campaign_type: number;
};

type BiddingEfficiencyRow = {
  BidPriceBucket: string;
  AvgBidPriceCpmMicroEur: number;
  AvgImpValueMicroEur: number;
  AvgMaxBidMicroEur: number;
  AvgSpendAdjustment: number;
  AvgCorrectionCoefficient: number;
  AvgEstimatedWinRate: number;
  AvgWinPriceCpmMicroEur: number;
  WinRate: number;
  Bids: number;
  Impressions: number;
  ImpPerBids: number;
  SupplyCostEur: number;
  RevenueEur: number;
  ProfitEur: number;
  EstimatedInfraCostEur: number;
  GrossProfitEur: number;
  Conversions: number;
  ApConversions: number;
  AvgEstimatedIpm: number;
  ApIpm: number;
  ApCpiEur: number;
  PredictedBidCpiEur: number;
  PredictedImpCpiEur: number;
};

type BiddingEfficiencyResponse = {
  params: BiddingEfficiencyParams;
  meta: MetaQueryData;
  results: BiddingEfficiencyRow[];
};
type AnalysisRow = {
  bid_day: string;
  bid_hour: string;
  dimension_type: string;
  dimension_value: string;
  metric_type: string;
  metric_value: string;
  aggregation_level: AggregationLevel;
};

type CampaignFilterRow = {
  reason: string;
  count: number;
  timestamp: string;
};

// User type (for experiments owner/author)
export type User = {
  id: number;
  name: string;
  admin?: boolean;
  email?: string;
  slack_user_id?: string;
};

export type Notification = {
  id: number;
  type: string;
  title: string;
  body?: string;
  link: string;
  read_at?: string;
  created_at: string;
  actor?: User;
};

export type NotificationsResponse = {
  items: Notification[];
  unread_count: number;
  has_more: boolean;
};

// Experiment Type enum
export enum ExperimentType {
  Global = 1, // Uses prediction flights
  Custom = 2, // Uses campaigns
}

export enum ExperimentOutcome {
  SignificantlyPositive = "significantly_positive",
  SignificantlyNegative = "significantly_negative",
  FlatNeutral = "flat_neutral",
  Inconclusive = "inconclusive",
}

export enum ExperimentRolloutStatus {
  RolledOut = "rolled_out",
  CreatedFollowUpExperiment = "created_follow_up_experiment",
  LearningDocumented = "learning_documented",
}

// What happened to a rolled-out change after it shipped. An absent value means nobody
// has checked yet, which is deliberately distinct from StillLive.
export enum ExperimentRolloutDurability {
  StillLive = "still_live",
  Reverted = "reverted",
  ClientChurned = "client_churned",
}

// Variant type for experiments
export type ExperimentVariant = "test" | "control";

// Campaign param type (key-value, response from API)
export type CampaignParam = {
  id: number;
  campaign_id: number;
  param_name: string;
  param_value: string;
};

// Prediction flight param type (predictor integer ID, response from API)
export type PredictionFlightParam = {
  id: number;
  prediction_flight_id: number;
  predictor_type_id: number;
  param_value: string;
};

// Experiment Campaign type
export type ExperimentCampaign = {
  id: number;
  campaign_id: number;
  experiment_id: number;
  control_campaign_id?: number;
  test_group?: number; // 1-based test-group index for test campaigns
  variant: ExperimentVariant;
  traffic_allocation: number; // Percentage as integer (e.g., 5000 = 50.00%)
  params?: CampaignParam[];
};

// Experiment Prediction Flight type
export type ExperimentPredictionFlight = {
  id: number;
  prediction_flight_id: number;
  experiment_id: number;
  variant: ExperimentVariant;
  traffic_allocation: number; // Percentage as integer (e.g., 5000 = 50.00%)
  params?: PredictionFlightParam[];
};

// Prediction Flight option from BigQuery
export type PredictionFlightOption = {
  id: number;
  name: string;
};

export type PredictionFlightsResponse = {
  meta: MetaQueryData;
  results: PredictionFlightOption[];
};

export type ExperimentScopeFilter = {
  kind: "audience_id" | "campaign_type" | "campaign_id" | "budget_objective";
  value: string;
};

export type ExperimentImportedPredictorConfig = {
  predictor_type: number;
  parameters?: unknown;
  repo?: string;
  branch?: string;
};

export type ExperimentImportedPredictionFlight = {
  prediction_flight_id: number;
  name: string;
  variant: ExperimentVariant;
  predictor_configs?: ExperimentImportedPredictorConfig[];
};

export type ExperimentImportMetadata = {
  source_name?: string;
  source_status?: string;
  source_description?: string;
  prediction_flights?: ExperimentImportedPredictionFlight[];
};

export type GlobalExperimentOption = {
  id: number;
  name: string;
};

export type GlobalExperimentOptionsResponse = {
  meta: MetaQueryData;
  results: GlobalExperimentOption[];
};

export type GlobalExperimentImportResponse = {
  imported_global_experiment_id: number;
  name: string;
  start_date: string;
  end_date: string;
  traffic_per_flight: number;
  import_last_synced_at: string;
  scope_filters?: ExperimentScopeFilter[];
  import_metadata?: ExperimentImportMetadata;
  prediction_flights: GlobalExperimentImportPredictionFlight[];
};

// Experiment Note type
export type ExperimentNote = {
  id: number;
  experiment_id: number;
  comment?: string;
  author_id: number;
  parent_id?: number;
  created_at: string;
  updated_at: string;
  author?: User;
  replies?: ExperimentNote[];
};

// Experiment Tag type. experiment_count is only present on the tag catalogue endpoint,
// not on the tags embedded in an experiment.
export type ExperimentTag = {
  id: number;
  name: string;
  experiment_count?: number;
};

export type ExperimentTagsResponse = {
  data: ExperimentTag[];
};

export type ExperimentTagsListParams = {
  search?: string;
  limit?: number;
};

export type SetExperimentTagsRequest = {
  tags: string[];
};

// Experiment type matching backend model
export type Experiment = {
  id: number;
  name: string;
  type: ExperimentType;
  hypothesis: string;
  organizations: number[];
  audiences: number[];
  start_date?: string;
  end_date?: string;
  external_links?: string[];
  conclusion?: string;
  outcome?: ExperimentOutcome;
  concluded_at?: string;
  rollout_status?: ExperimentRolloutStatus;
  rollout_at?: string;
  rollout_additional_info?: string;
  rollout_durability_status?: ExperimentRolloutDurability;
  rollout_durability_ended_at?: string;
  rollout_durability_note?: string;
  rollout_durability_recorded_at?: string;
  owner: number;
  imported_global_experiment_id?: number;
  import_last_synced_at?: string;
  scope_filters?: ExperimentScopeFilter[];
  import_metadata?: ExperimentImportMetadata;
  repo_branch_pairs?: ExperimentRepoBranchPair[];
  diagnosis_all_runs_succeeded: boolean;
  diagnosis_summary?: string;
  diagnosis_last_completed_at?: string;
  created_at: string;
  updated_at: string;
  owner_user?: User;
  campaigns?: ExperimentCampaign[];
  prediction_flights?: ExperimentPredictionFlight[];
  notes?: ExperimentNote[];
  tags?: ExperimentTag[];
};

export type ExperimentRepoBranchPair = {
  repo: string;
  branch: string;
};

export type ExperimentsListResponse = {
  data: Experiment[];
  meta: {
    page: number;
    page_size: number;
    total: number;
    total_page: number;
  };
};

export type ExperimentsListParams = {
  page: number;
  page_size: number;
  search?: string;
  tag?: string;
};

export type ExperimentVersionFieldChange = {
  from: unknown;
  to: unknown;
};

export type ExperimentVersionChange = {
  id: number;
  change_set_id: number;
  item_type: string;
  item_id: string;
  before_state: unknown;
  after_state: unknown;
  changed_fields: string[];
  object_changes: Record<string, ExperimentVersionFieldChange>;
};

export type ExperimentVersion = {
  id: number;
  aggregate_type: string;
  aggregate_id: string;
  event: "create" | "update" | "delete";
  actor_user_id?: number;
  actor_name?: string;
  actor_email?: string;
  occurred_at: string;
  metadata: Record<string, unknown>;
  changes?: ExperimentVersionChange[];
};

export type ExperimentVersionsResponse = {
  data: ExperimentVersion[];
};

export type ExperimentDiagnosticsMetricType = "bids" | "impressions" | "revenue_eur";
export type ExperimentDiagnosticsMemberType = "campaign" | "prediction_flight";

export type ExperimentDiagnosticsPoint = {
  metric_id: number;
  run_id: number;
  run_hour: string;
  completed_at: string;
  value: number;
  is_acknowledged: boolean;
  acknowledged_by?: number;
  acknowledged_by_name?: string;
};

export type ExperimentDiagnosticsSeries = {
  metric_type: ExperimentDiagnosticsMetricType;
  member_type: ExperimentDiagnosticsMemberType;
  member_id: number;
  variant: string;
  points: ExperimentDiagnosticsPoint[];
};

export type ExperimentDiagnosticsDailySpendPoint = {
  id: number;
  date: string;
  actual_spend: number;
  target_spend?: number;
  delta_pct?: number;
  is_issue: boolean;
  is_acknowledged: boolean;
  acknowledged_by?: number;
  acknowledged_by_name?: string;
};

export type ExperimentDiagnosticsDailySpendSeries = {
  member_type: ExperimentDiagnosticsMemberType;
  member_id: number;
  variant: string;
  points: ExperimentDiagnosticsDailySpendPoint[];
};

export type ExperimentDiagnosticsData = {
  experiment_id: number;
  series: ExperimentDiagnosticsSeries[];
  daily_spend_series: ExperimentDiagnosticsDailySpendSeries[];
};

export type ExperimentDiagnosticsResponse = {
  data: ExperimentDiagnosticsData;
};

export type StabilityMetric = "mdape" | "wape";

export type StabilityPoint = {
  last_period_end: string; // YYYY-MM-DD
  mdape: number | null;
  wape: number | null;
};

export type StabilityModelTimeline = {
  name: string;
  points: StabilityPoint[];
};

export type StabilityReport = {
  root: string;
  models: StabilityModelTimeline[];
};

export type StabilityResponse = {
  data: StabilityReport;
};

export type VertexRunState = "SUCCEEDED" | "CANCELLED" | "FAILED" | "RUNNING" | string;

export type VertexRun = {
  job_name: string;
  display_name: string;
  state: VertexRunState;
  location: string;
  model: string;
  version: string;
  create_time: string;
  start_time?: string;
  end_time?: string;
  console_url: string;
  schedule_url?: string;
  is_migration?: boolean;
};

export type VertexRunBucket = {
  state: VertexRunState;
  runs: VertexRun[];
};

export type ExperimentVertexRunsResponse = {
  data: {
    test: VertexRunBucket[];
    control: VertexRunBucket[];
  };
};

export type VertexMonitoredModelsResponse = {
  data: {
    models: string[];
  };
};

export type ExperimentDiagnosticsRunStatus = "success" | "failed";

export type ExperimentDiagnosticsRun = {
  id: number;
  run_hour: string;
  status: ExperimentDiagnosticsRunStatus;
  started_at: string;
  completed_at: string;
  active_experiment_count: number;
  error_message?: string;
  created_at: string;
};

export type ExperimentDiagnosticsRunResponse = {
  data: ExperimentDiagnosticsRun;
};

export type ScheduleExperimentAnalysisRequest = {
  excluded_dates?: string[];
  overwrite_artifacts?: boolean;
};

export type ExperimentAnalysisRunStateType =
  | "SCHEDULED"
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED"
  | "CRASHED"
  | string;

export type AnalysisMetricOutcome = "won" | "lost" | "neutral" | "inconclusive";

export type ExperimentAnalysisMetricResult = {
  won: number;
  lost: number;
  neutral: number;
  inconclusive?: number;
};

export type ExperimentAnalysisSummaryMetrics = {
  XPM: ExperimentAnalysisMetricResult;
  XPC: ExperimentAnalysisMetricResult;
};

export type ExperimentAnalysisRevenueSummaryMetrics = {
  RPM: ExperimentAnalysisMetricResult;
  ROAS: ExperimentAnalysisMetricResult;
};

export type ExperimentAnalysisSummaryTest = {
  primary: ExperimentAnalysisSummaryMetrics;
  secondary: ExperimentAnalysisSummaryMetrics;
  // Absent in results from before the revenue analysis was added.
  revenue?: ExperimentAnalysisRevenueSummaryMetrics;
};

// Outcome counts live under `results`, keyed by test group: test-flight id (global) or 0-based
// positional column index (custom). `status` stays top-level.
export type ExperimentAnalysisSummary = {
  status?: string;
  results?: Record<string, ExperimentAnalysisSummaryTest>;
};

export type ExperimentAnalysisKde = {
  x: number[];
  density: number[];
};

// Stats are absent when the comparison is inconclusive (only `outcome` is present): too few converters, or no
// data for the control or the test.
export type ExperimentAnalysisTestMetricDetails = {
  outcome?: AnalysisMetricOutcome;
  kde?: ExperimentAnalysisKde;
  diff_mean?: number;
  diff_median?: number;
  diff_ci_lower?: number;
  diff_ci_upper?: number;
  p_test_better?: number;
  lift_mean_pct?: number;
  lift_median_pct?: number;
  lift_ci_lower_pct?: number;
  lift_ci_upper_pct?: number;
};

export type ExperimentAnalysisTestMetrics = {
  XPM: ExperimentAnalysisTestMetricDetails;
  XPC: ExperimentAnalysisTestMetricDetails;
};

export type ExperimentAnalysisRevenueTestMetrics = {
  RPM: ExperimentAnalysisTestMetricDetails;
  ROAS: ExperimentAnalysisTestMetricDetails;
};

// Empty when the variant has no data in that comparison (e.g. a test flight that didn't run in a campaign).
// The revenue and converter fields are absent in results from before the revenue analysis was added.
export type ExperimentAnalysisCampaignStats = {
  impressions?: number;
  supply_cost_eur?: number;
  ap_conversions?: number;
  secondary_ap_conversions?: number;
  ap_customer_revenue_eur?: number;
  // Unique users with a 2nd AP conversion / with revenue (impressions with one, taken as one user each).
  secondary_converters?: number;
  revenue_converters?: number;
  IPC?: number;
  APC?: number;
  ROAS?: number;
};

export type ExperimentAnalysisTestStats = ExperimentAnalysisCampaignStats & {
  primary?: ExperimentAnalysisTestMetrics;
  secondary?: ExperimentAnalysisTestMetrics;
  revenue?: ExperimentAnalysisRevenueTestMetrics;
};

export type ExperimentAnalysisCampaign = {
  control: ExperimentAnalysisCampaignStats;
  tests: Record<string, ExperimentAnalysisTestStats>;
};

export type ExperimentAnalysisExclusion = {
  campaign: number;
  primary_prediction_flight_id: number;
  supply_cost_eur: number;
  reason: string;
};

export type ExperimentAnalysisResultArtifact = {
  experiment_id: number;
  run_date: string;
  start: string;
  end: string;
  type: string;
  variants: Record<string, number[]>;
  summary: ExperimentAnalysisSummary;
  campaigns: Record<string, ExperimentAnalysisCampaign>;
  exclusions?: ExperimentAnalysisExclusion[];
};

export type ExperimentAnalysisRun = {
  id?: number;
  flow_run_id: string;
  name?: string;
  state_type: ExperimentAnalysisRunStateType;
  state_name: string;
  state_message?: string;
  state_timestamp?: string;
  created?: string;
  expected_start_time?: string;
  start_time?: string;
  end_time?: string;
  total_run_time?: number;
  terminal: boolean;
  prefect_ui_url?: string;
  result_path?: string;
  result_artifact?: ExperimentAnalysisResultArtifact;
};

export type ExperimentAnalysisRunResponse = {
  data: ExperimentAnalysisRun;
};

export type ExperimentAnalysisRunsResponse = {
  data: ExperimentAnalysisRun[];
};

export type CampaignParamInput = {
  param_name: string;
  param_value: string;
};

export type PredictionFlightParamInput = {
  predictor_type_id: number;
  param_value: string;
};

export type CampaignInput = {
  campaign_id: number;
  control_campaign_id?: number;
  test_group?: number; // 1-based test-group index for test campaigns
  variant: ExperimentVariant;
  traffic_allocation: number; // Percentage as integer (e.g., 5000 = 50.00%)
  params?: CampaignParamInput[];
};

export type PredictionFlightInput = {
  prediction_flight_id: number;
  variant: ExperimentVariant;
  traffic_allocation: number; // Percentage as integer (e.g., 5000 = 50.00%)
  params?: PredictionFlightParamInput[];
};

export type GlobalExperimentImportPredictionFlight = {
  prediction_flight_id: number;
  variant: ExperimentVariant;
  params?: PredictionFlightParamInput[];
};

export type CreateExperimentRequest = {
  name: string;
  type: ExperimentType;
  hypothesis: string;
  owner: number;
  organizations?: number[];
  audiences?: number[];
  start_date?: string;
  end_date?: string;
  external_links?: string[];
  conclusion?: string;
  outcome?: ExperimentOutcome;
  rollout_additional_info?: string;
  imported_global_experiment_id?: number;
  import_last_synced_at?: string;
  scope_filters?: ExperimentScopeFilter[];
  import_metadata?: ExperimentImportMetadata;
  campaigns?: CampaignInput[];
  prediction_flights?: PredictionFlightInput[];
  repo_branch_pairs?: ExperimentRepoBranchPair[];
};

export type UpdateExperimentRequest = CreateExperimentRequest;

export type SetExperimentRolloutRequest = {
  rollout_status: ExperimentRolloutStatus;
  rollout_at: string;
  rollout_additional_info: string;
};

// rollout_durability_ended_at is when the rolled-out change stopped being live — the
// revert, or the churn that left no traffic. It is optional: still live never has one, and
// a one-click confirmation omits it so the server stamps the write instant rather than
// trusting the browser's clock. The experiment page sends it explicitly for a revert or
// churn that happened earlier.
export type SetExperimentRolloutDurabilityRequest = {
  rollout_durability_status: ExperimentRolloutDurability;
  rollout_durability_ended_at?: string;
  rollout_durability_note?: string;
};

export type CreateCampaignRequest = {
  campaign_id: number;
  control_campaign_id?: number;
  variant: ExperimentVariant;
  traffic_allocation: number; // Percentage as integer (e.g., 5000 = 50.00%)
};

export type CreateNoteRequest = {
  comment: string;
  parent_id?: number;
};

export type UpdateNoteRequest = {
  comment: string;
};

export type ExperimentDataRow = {
  bid_day: string;
  campaign: number;
  ap_conversions: number;
  ap_ecpx_achievement: number;
  second_ap_conversions: number;
  second_ap_ecpx_achievement: number;
  revenue: number;
};

export type ExperimentDataResponse = {
  meta: MetaQueryData;
  results: ExperimentDataRow[];
};

export type ExperimentsDailyDeliveryRow = {
  date: string;
  campaign_id?: number;
  prediction_flight_id?: number;
  revenue: number;
  delivery: number;
};

export type ExperimentsDailyDeliveryTargetRow = {
  date: string;
  campaign_id?: number;
  prediction_flight_id?: number;
  variant: string;
  target_spend: number;
};

export type ExperimentsDailyDeliveryResponse = {
  start_date: string;
  end_date: string;
  rows: ExperimentsDailyDeliveryRow[];
  targets: ExperimentsDailyDeliveryTargetRow[];
};

type ExperimentDataParams = {
  audience_ids?: string;
  campaigns?: string;
  start_date?: string;
  end_date?: string;
};

type MetricRow = {
  category: string;
  metric: string;
  values: { [key: string]: number };
  format: string;
  low_threshold?: number;
  high_threshold?: number;
  low_thresholds?: { [key: string]: number };
  high_thresholds?: { [key: string]: number };
  low_threshold_display?: string;
  high_threshold_display?: string;
};

type BusinessMetricsResponse = {
  months: string[];
  rows: MetricRow[];
};

type ExperimentsDailyDeliveryParams = {
  experiment_id?: string;
  start_date: string;
  end_date: string;
  campaign_ids?: string;
  prediction_flight_ids?: string;
};

type ExperimentTimelineSummaryParams = {
  from?: string;
  to?: string;
};

type LearningLoopClosureBaseline = {
  experiment_count: number;
  median_runtime_seconds: number;
  median_closure_seconds: number;
  median_end_to_conclusion_seconds: number;
  median_conclusion_to_rollout_seconds: number;
  experiments: LearningLoopClosureExperiment[];
};

type LearningLoopClosureExperiment = {
  experiment_id: number;
  experiment_name: string;
  start_date: string;
  end_date: string;
  concluded_at: string;
  rollout_at: string;
  runtime_seconds: number;
  closure_seconds: number;
  end_to_conclusion_seconds: number;
  conclusion_to_rollout_seconds: number;
};

type ExperimentTimelineSummaryData = {
  learning_loop_closure_baseline: LearningLoopClosureBaseline;
};

type ExperimentTimelineSummaryResponse = {
  data: ExperimentTimelineSummaryData;
};

type ExperimentTimelineStatus = "active" | "scheduled" | "waiting_for_outcome" | "concluded";

type ExperimentTimelineExperimentType = "custom" | "global";

type ExperimentTimelineSegment = {
  experiment_id: number;
  experiment_name: string;
  experiment_type: ExperimentTimelineExperimentType;
  status: ExperimentTimelineStatus;
  start_at: string;
  end_at: string;
  experiment_start_at?: string;
  experiment_end_at?: string;
  lane_index: number;
  lane_count: number;
  membership_source: string;
  variant?: ExperimentVariant;
  traffic_allocation?: number;
};

type ExperimentTimelineCampaign = {
  campaign_id: number;
  campaign_name: string;
  campaign_type: "install" | "ctv_install";
  audience_id: number;
  audience_name: string;
  organization_id: number;
  budget_objective?: string;
  segments: ExperimentTimelineSegment[] | null;
};

type ExperimentTimelineData = {
  window_start: string;
  window_end: string;
  campaigns: ExperimentTimelineCampaign[];
};

type ExperimentTimelineParams = {
  from?: string;
  to?: string;
};

type ExperimentTimelineResponse = {
  data: ExperimentTimelineData;
};

type JiraMetricsSummaryParams = {
  from?: string;
  to?: string;
  group_by?: "week" | "2week" | "4week" | "month";
  done_only?: boolean;
  projects?: string;
};

type JiraMetricsTicketParams = {
  from?: string;
  to?: string;
  done_only?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  projects?: string;
};

type JiraMetricsProjectsResponse = {
  data: string[];
};

type JiraMetricsKpi = {
  avg_cycle_seconds: number;
  median_cycle_seconds: number;
  avg_review_seconds: number;
  avg_in_progress_seconds: number;
};

type JiraMetricsCycleTrendPoint = {
  period: string;
  avg_cycle_seconds: number;
  median_cycle_seconds: number;
};

type JiraMetricsThroughputPoint = {
  period: string;
  done_count: number;
};

type JiraMetricsStageCompositionPoint = {
  period: string;
  avg_in_progress_seconds: number;
  avg_review_seconds: number;
  avg_approved_to_done_seconds: number;
};

type JiraMetricsHistogramPoint = {
  label: string;
  count: number;
};

type JiraMetricsCycleTimePercentileTrendPoint = {
  period: string;
  min: number;
  p25: number;
  p50: number;
  p75: number;
  max: number;
};

type JiraMetricsScatterPoint = {
  ticket_key: string;
  review_seconds: number;
  cycle_seconds: number;
};

type JiraMetricsAgingWIPPoint = {
  ticket_key: string;
  assignee?: string;
  open_days: number;
  status?: string;
};

type JiraMetricsControlChartPoint = {
  ticket_key: string;
  moved_to_done: string;
  cycle_seconds: number;
};

type JiraMetricsCFDPoint = {
  period: string;
  in_progress_count: number;
  review_requested_count: number;
  in_review_count: number;
  changes_requested_count: number;
  changes_approved_count: number;
};

type JiraMetricsSLAFunnelPoint = {
  stage: string;
  count: number;
};

type JiraMetricsSummaryData = {
  kpi: JiraMetricsKpi;
  cycle_trend: JiraMetricsCycleTrendPoint[];
  throughput_trend: JiraMetricsThroughputPoint[];
  stage_composition: JiraMetricsStageCompositionPoint[];
  histogram: JiraMetricsHistogramPoint[];
  cycle_time_percentile_trend: JiraMetricsCycleTimePercentileTrendPoint[];
  scatter: JiraMetricsScatterPoint[];
  aging_wip: JiraMetricsAgingWIPPoint[];
  control_chart: JiraMetricsControlChartPoint[];
  cfd: JiraMetricsCFDPoint[];
  sla_funnel: JiraMetricsSLAFunnelPoint[];
};

type JiraMetricsSummaryResponse = {
  data: JiraMetricsSummaryData;
};

type JiraMetricsTicket = {
  id: number;
  ticket_key: string;
  summary?: string;
  assignee?: string;
  project_key?: string;
  issue_type?: string;
  current_status?: string;
  first_time_in_progress?: string;
  moved_to_changes_approved?: string;
  moved_to_done?: string;
  total_time_in_progress_seconds?: number;
  total_time_in_review_seconds?: number;
  cycle_time_seconds?: number;
  source_updated_at?: string;
  last_synced_at: string;
  created_at: string;
  updated_at: string;
};

type JiraMetricsTicketsResponse = {
  data: JiraMetricsTicket[];
  meta: {
    page: number;
    page_size: number;
    total: number;
    total_page: number;
  };
};

type JiraMetricsSyncRun = {
  id: number;
  started_at: string;
  completed_at: string;
  rows_fetched: number;
  rows_upserted: number;
  cost_usd: number;
  took_seconds: number;
  created_at: string;
};

type JiraMetricsSyncRunsResponse = {
  data: JiraMetricsSyncRun[];
};

type TeamGoalsSummaryResponse = {
  experiments: { week_start_utc: string; concluded_count: number }[];
  tasks: { week_start_utc: string; median_cycle_seconds: number }[];
};

type CPAClientInfo = {
  org_id: number;
  org_name: string;
  first_campaign_date: string;
};

type CPAWeekPoint = {
  week_number: number;
  week_start: string;
  cpa_achievement: number;
  ap_conversions: number;
  revenue: number;
};

type CPAOrgData = CPAClientInfo & { weeks: CPAWeekPoint[] };

type CPAClientsResponse = { data: CPAClientInfo[] };

type CPAResponse = { data: CPAOrgData[] };

type EvidenceGatePreRegistration = "pass" | "fail" | "unverifiable";

type EvidenceGateExperiment = {
  id: number;
  name: string;
  type: "global" | "custom";
  owner_name: string;
  start_date: string;
  end_date: string;
  concluded_at: string;
  rollout_recorded: boolean;
  runtime_days: number;
  trust_runtime: boolean;
  trust_pre_registration: EvidenceGatePreRegistration;
  pre_registration_late_days: number;
  recorded_after_start: boolean;
  trusted: boolean;
  verdict: string;
  verdict_detail: string;
};

type EvidenceGatePeriod = {
  from: string;
  to: string;
  raw: number;
  raw_per_week: number;
  trusted: number;
  trusted_per_week: number;
  awaiting_rollout: number;
};

type EvidenceGateWeeklyPoint = {
  week_start_utc: string;
  raw: number;
  trusted: number;
};

type EvidenceGateRollout = {
  experiment_id: number;
  name: string;
  start_date: string;
  end_date: string;
  rollout_at: string;
  durable_at: string;
  durability_proven: boolean;
  overlapped_by: number[];
  baseline_spend_eur: number;
  week2_spend_eur: number;
  week2_installs: number;
  week1_install_lift: number | null;
  week1_action_lift: number | null;
  week2_install_lift: number | null;
  week2_action_lift: number | null;
  measurable: boolean;
  is_win: boolean;
};

type EvidenceGatesCheck1Response = {
  rollouts_required: number;
  wins_required: number;
  lift_threshold: number;
  durability_days: number;
  gate_date: string;
  last_durable_rollout: string;
  realized_available: boolean;
  rolled_out: number;
  wins: number;
  unmeasurable: number;
  rollouts: EvidenceGateRollout[];
};

type EvidenceGatesCheck2Response = {
  baseline: EvidenceGatePeriod;
  evaluation: EvidenceGatePeriod;
  evaluation_to_date: EvidenceGatePeriod;
  multiplier: number;
  target_per_week: number;
  target_raw_per_week: number;
  min_runtime_days: number;
  pre_registration_grace_hours: number;
  weekly: EvidenceGateWeeklyPoint[];
  experiments: EvidenceGateExperiment[];
};

export type {
  AnalysisParams,
  AnalysisRow,
  ApiResponse,
  AudienceCampaignOption,
  AudienceCampaignOptionData,
  AudienceOptionsParams,
  AudinceCampaignOptionResponse,
  BiddingEfficiencyParams,
  BiddingEfficiencyResponse,
  BiddingEfficiencyRow,
  BusinessMetricsResponse,
  CampaignFilterParams,
  CampaignFilterRow,
  ClientMetricsRow,
  ClientRow,
  CPAClientInfo,
  CPAClientsResponse,
  CPAOrgData,
  CPAResponse,
  CPAWeekPoint,
  EvidenceGateExperiment,
  EvidenceGatePeriod,
  EvidenceGatePreRegistration,
  EvidenceGateRollout,
  EvidenceGatesCheck1Response,
  EvidenceGatesCheck2Response,
  EvidenceGateWeeklyPoint,
  ExperimentDataParams,
  ExperimentsDailyDeliveryParams,
  ExperimentTimelineCampaign,
  ExperimentTimelineData,
  ExperimentTimelineExperimentType,
  ExperimentTimelineParams,
  ExperimentTimelineResponse,
  ExperimentTimelineSegment,
  ExperimentTimelineStatus,
  ExperimentTimelineSummaryData,
  ExperimentTimelineSummaryParams,
  ExperimentTimelineSummaryResponse,
  Granularity,
  JiraMetricsAgingWIPPoint,
  JiraMetricsCFDPoint,
  JiraMetricsControlChartPoint,
  JiraMetricsCycleTimePercentileTrendPoint,
  JiraMetricsHistogramPoint,
  JiraMetricsKpi,
  JiraMetricsProjectsResponse,
  JiraMetricsScatterPoint,
  JiraMetricsSLAFunnelPoint,
  JiraMetricsStageCompositionPoint,
  JiraMetricsSummaryData,
  JiraMetricsSummaryParams,
  JiraMetricsSummaryResponse,
  JiraMetricsSyncRun,
  JiraMetricsSyncRunsResponse,
  JiraMetricsThroughputPoint,
  JiraMetricsTicket,
  JiraMetricsTicketParams,
  JiraMetricsTicketsResponse,
  LearningLoopClosureBaseline,
  LearningLoopClosureExperiment,
  MetaQueryData,
  MetricRow,
  OverviewResponse,
  PerformanceParams,
  QueryIntervalParams,
  ResourceOptions,
  RevenueByAdTypeRow,
  RevenueHistoryRow,
  RevenueOverviewRow,
  RevenueRow,
  TeamGoalsSummaryResponse,
};
